import { EVENT_DELAY } from '@src/config/constants'
import { CONFIG_KEY_FB_PIXEL_ID } from '@src/config/env'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { initializeScript } from '@src/telemetry/integrations/provider-utils/initialize-script'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { getStorefrontIdProperty } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

const NEW_FB_PIXEL_ID_V2 = '1900161340840474'
const NEW_FB_PIXEL_ID_V3 = '873621187951720'

const getUserTrackingData = () => {
  return {
    em:
      getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)?.trim() ??
      getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL)?.trim() ??
      '',
    external_id: getlbUserId()?.trim() ?? '',
  }
}

export const FbPixel: TelemetryIntegration = {
  // Note: Not handling page view event here explicitly, this is because fb pixel script takes care
  // of triggering Page View event in case of browser history state change.
  //  https://developers.facebook.com/ads/blog/post/v2/2017/05/29/tagging-a-single-page-application-facebook-pixel/
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.FB_PIXEL,
    featureGateName: 'ssr_telemetry_fb_pixel',
  },

  initialize: async () => {
    await initializeScript({
      key: 'tracking-provider-fb',
      domain: TELEMETRY_DOMAINS.FB_PIXEL,
      path: '/en_US/fbevents.js',
      injectAt: 'beforeFirstScript',
      maxDelay: 1000,
    })

    if (window.fbq) return
    window.fbq = function (...args) {
      if (window.fbq && window.fbq.callMethod) {
        window.fbq.callMethod(...args)
      } else {
        window.fbq?.queue?.push(args)
      }
    }
    const fbq = window.fbq
    if (!window._fbq) window._fbq = fbq
    fbq.push = fbq
    fbq.loaded = !0
    fbq.version = '2.0'
    fbq.queue = []

    window.fbq('init', CONFIG_KEY_FB_PIXEL_ID, getUserTrackingData())
    window.fbq('init', NEW_FB_PIXEL_ID_V2, getUserTrackingData())
    window.fbq('init', NEW_FB_PIXEL_ID_V3, getUserTrackingData())

    // FIX: Use trackSingle to ensure each pixel receives PageView individually
    // This prevents the race condition where blotout pixels miss PageView events
    window.fbq('trackSingle', CONFIG_KEY_FB_PIXEL_ID, 'PageView')
    window.fbq('trackSingle', NEW_FB_PIXEL_ID_V2, 'PageView')
    window.fbq('trackSingle', NEW_FB_PIXEL_ID_V3, 'PageView')
  },

  events: {
    [TELEMETRY_EVENT_NAMES.LEAD]: properties => {
      logFbPixelEvent({
        eventType: 'Lead',
        contentIds: [],
        contentType: 'lead',
        contentName: properties.contentName,
        contentCategory: '',
        value: 0,
      })
    },
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      const value = properties?.value ?? properties?.price

      logFbPixelEvent({
        eventType: 'AddToCart',
        contentIds: [properties.variantId],
        contentType: 'product',
        contentName:
          properties.productTitle && properties.variantTitle
            ? properties.productTitle + ' - ' + properties.variantTitle
            : '',
        contentCategory: properties.productType,
        value: value ? formatRevenue(Number(value)) : 0,
        numItems: properties.quantity || 1,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW](properties) {
      const isPartnerProductPDPPage = properties.isPartnerProductPDPPage
      const isSingleVariant = properties.variantIds.length === 1
      if (!isPartnerProductPDPPage && properties.productTitle) {
        if (isSingleVariant) {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: properties.variantIds.length > 0 && properties.variantIds[0] ? [properties.variantIds[0]] : [],
            contentType: 'product',
            contentName:
              properties.productTitle && properties.variantTitle
                ? properties.productTitle + ' - ' + properties.variantTitle
                : '',
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
          })
        } else {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.productId.toString()],
            contentType: 'product_group',
            contentName: properties.productTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
          })
        }
      }
    },

    [TELEMETRY_EVENT_NAMES.VARIANT_SELECT]: properties => {
      const isSingleVariant = properties.variantIds.length === 1
      if (!properties.is_product_card && properties.productTitle) {
        if (isSingleVariant && properties.variantIds.length > 0 && properties.variantIds[0]) {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.variantIds[0]],
            contentType: 'product',
            contentName: properties.productTitle + ' - ' + properties.variantTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
          })
        } else {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.spid],
            contentType: 'product_group',
            contentName: properties.productTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
          })
        }
      }
    },
  },
}

const logFbPixelEvent = ({
  eventType,
  contentIds,
  contentType,
  contentName,
  contentCategory,
  value,
  numItems,
  eventId,
}: {
  eventType: string
  contentIds: string[]
  contentType: string
  contentName: string
  contentCategory: string
  value: number
  numItems?: number
  eventId?: string
}) => {
  const localisationData = getLocalisationDataFromStorage()
  executeFireAndForgotTask(() => {
    if (window.fbq) {
      window.fbq('track', eventType, {
        content_ids: contentIds,
        content_type: contentType,
        content_name: contentName,
        content_category: contentCategory,
        currency: localisationData?.currency ?? 'USD',
        country: localisationData?.country,
        value: value,
        num_items: numItems,
        event: eventType,
        eventID: eventId,
        ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
      })
    }
  }, EVENT_DELAY)
}

export const formatRevenue = (price: number | null) => {
  if (price != null) {
    return price
  }
  return 0
}
